import React, { useCallback, useEffect, useMemo, useState } from 'react';

import styled from 'styled-components';

import ClearIcon from '@mui/icons-material/Clear';
import DragIndicatorOutlinedIcon from '@mui/icons-material/DragIndicatorOutlined';
import SaveIcon from '@mui/icons-material/Save';
import SettingsIcon from '@mui/icons-material/Settings';

import { Box } from '@mui/material';
import { FormControlLabel, Menu, Switch, Tooltip } from 'components';

import { TableColumn } from './types';

import {
  DragDropContext,
  Draggable,
  DraggableProvided,
  Droppable,
  DroppableProvided,
  useTranslation,
} from 'third-party';

const SettingsTrigger = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 8px;
  cursor: pointer;
  background-color: rgba(230, 106, 16, 0.1);
  transition: background-color 0.3s ease, transform 0.3s ease;

  svg {
    color: ${props => props.theme.custom.palette.primaryOrange};
    font-size: 1.375rem;
  }

  &:hover {
    transform: translateY(-2px);
    background-color: rgba(230, 106, 16, 0.15);
  }

  &:active {
    transform: translateY(0);
    background-color: rgba(230, 106, 16, 0.2);
  }
`;

const Wrapper = styled.div`
  padding: 16px 0;
  display: flex;
  flex-direction: column;
  width: 250px;
`;

const TopActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 16px 12px 16px;
  margin-bottom: 8px;
`;

const IconAction = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${props => props.theme.custom.palette.primaryOrange};
  transition: opacity 0.2s ease, transform 0.2s ease;

  svg {
    font-size: 1.25rem;
  }

  &:hover {
    transform: scale(1.1);
    opacity: 0.7;
  }
`;

const SectionTitle = styled.div`
  padding: 0 16px 12px 16px;
  font-size: 0.6875rem;
  font-weight: 700;
  color: ${props => props.theme.custom.palette.textGray};
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const ColumnRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${props => props.theme.custom.palette.bgLight};
  }
`;

const ColumnLabel = styled.span`
  flex: 1;
  font-size: 0.8125rem;
  font-weight: 500;
  color: ${props => props.theme.custom.palette.textDark};
`;

type Props<ItemT> = {
  columns: TableColumn<ItemT>[];
  onColumnVisibilityChanged: (columnId: string, value: boolean) => void;
  onColumnOrderChanged?: (columns: TableColumn<ItemT>[]) => void;
  onSaveTableSettings?: (columns: TableColumn<ItemT>[]) => void;
  onResetTableSettings?: () => void;
  draggable?: boolean;
};

export const TableSettings = <ItemT,>({
  columns,
  onColumnVisibilityChanged,
  onSaveTableSettings,
  onResetTableSettings,
  onColumnOrderChanged,
  draggable = true,
}: Props<ItemT>) => {
  const { t } = useTranslation();
  const canBeHiddenColumns = useMemo(() => {
    return columns.filter(it => !it.cannotBeHidden);
  }, [columns]);

  const [popupAnchorEl, setPopupAnchorEl] = useState<null | Element>();
  const [draggableColumns, setDraggableColumns] = useState(canBeHiddenColumns);

  useEffect(() => {
    setDraggableColumns(canBeHiddenColumns);
  }, [canBeHiddenColumns]);

  const closePopup = useCallback(() => {
    setPopupAnchorEl(null);
  }, []);

  const onTriggerButtonClicked = (event: React.MouseEvent<HTMLDivElement>) => {
    setPopupAnchorEl(event.currentTarget);
  };

  const onDragEnd = (result: any) => {
    if (!draggable || !result.destination) return;
    const reorderedColumns = Array.from(draggableColumns);
    const [removed] = reorderedColumns.splice(result.source.index, 1);
    reorderedColumns.splice(result.destination.index, 0, removed);
    setDraggableColumns(reorderedColumns);
    if (onColumnOrderChanged) {
      onColumnOrderChanged(reorderedColumns);
    }
  };

  const handleColumnVisibilityChange = useCallback(
    (columnId: string, value: boolean) => {
      setDraggableColumns(prevColumns =>
        prevColumns.map(column =>
          column.dataId.toString() === columnId
            ? { ...column, hidden: !value }
            : column,
        ),
      );
      onColumnVisibilityChanged(columnId, value);
    },
    [onColumnVisibilityChanged],
  );

  return (
    <>
      <Tooltip title={t('table.settings')} arrow>
        <SettingsTrigger onClick={onTriggerButtonClicked}>
          <SettingsIcon />
        </SettingsTrigger>
      </Tooltip>
      <Menu
        keepMounted
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        anchorEl={popupAnchorEl}
        open={!!popupAnchorEl}
        onClose={closePopup}
        sx={{
          marginTop: '0.5rem',
          '& .MuiPaper-root': {
            borderRadius: '12px',
            border: '1px solid #E5E7EB',
            boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
          },
          '& .MuiList-root': {
            padding: 0,
          },
        }}
      >
        <Wrapper>
          <TopActions>
            {onSaveTableSettings ? (
              <Tooltip title={t('table.saveTableState')} arrow>
                <IconAction
                  onClick={() => {
                    onSaveTableSettings(
                      // @ts-ignore
                      draggableColumns.map(({ ref, formatter, ...rest }) => ({
                        ...rest,
                        order: draggableColumns.findIndex(
                          col => col.dataId === rest.dataId,
                        ),
                      })),
                    );
                    closePopup();
                  }}
                >
                  <SaveIcon />
                </IconAction>
              </Tooltip>
            ) : (
              <div />
            )}
            {onResetTableSettings ? (
              <Tooltip title={t('table.clearTableFilterState')} arrow>
                <IconAction
                  onClick={() => {
                    onResetTableSettings();
                    closePopup();
                  }}
                >
                  <ClearIcon />
                </IconAction>
              </Tooltip>
            ) : (
              <div />
            )}
          </TopActions>

          <SectionTitle>{t('table.columnVisibility')}</SectionTitle>

          {/* @ts-ignore react-redux v7 / react-beautiful-dnd compat */}
          <DragDropContext onDragEnd={onDragEnd}>
            {/* @ts-ignore */}
            <Droppable droppableId="columns">
              {(provided: DroppableProvided) => (
                // @ts-ignore
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  style={{ userSelect: 'none' }}
                >
                  {draggableColumns.map((it, index) => (
                    // @ts-ignore
                    <Draggable
                      key={it.dataId.toString()}
                      draggableId={it.dataId.toString()}
                      index={index}
                      isDragDisabled={!draggable}
                    >
                      {(provided: DraggableProvided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...(draggable ? provided.dragHandleProps : {})}
                        >
                          <ColumnRow>
                            <FormControlLabel
                              control={
                                <Switch
                                  size="small"
                                  checked={!it.hidden}
                                  onChange={event =>
                                    handleColumnVisibilityChange(
                                      it.dataId.toString(),
                                      event.target.checked,
                                    )
                                  }
                                />
                              }
                              label={
                                <ColumnLabel>
                                  {
                                    //@ts-ignore
                                    t(it.label)
                                  }
                                </ColumnLabel>
                              }
                              sx={{ margin: 0, gap: '12px', flex: 1 }}
                            />
                            {draggable && (
                              <Box
                                {...provided.dragHandleProps}
                                sx={{
                                  cursor: 'grab',
                                  display: 'flex',
                                  color: '#9CA3AF',
                                  fontSize: '1.125rem',
                                  flexShrink: 0,
                                }}
                              >
                                <DragIndicatorOutlinedIcon
                                  sx={{ fontSize: '1.125rem' }}
                                />
                              </Box>
                            )}
                          </ColumnRow>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </Wrapper>
      </Menu>
    </>
  );
};
